import { useState } from "react";
import type { Article, Category } from "@/mocks/contest";

function LikeButton({ article }: { article: Article }) {
  const [liked, setLiked] = useState(false);
  return (
    <button
      type="button"
      aria-label="点赞"
      onClick={() => setLiked((v) => !v)}
      className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition ${
        liked
          ? "bg-primary-500 text-background-50"
          : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
      }`}
    >
      <i className={liked ? "ri-heart-fill" : "ri-heart-3-line"} />
      {liked ? (Number(article.likes.replace("w", "")) + 1).toLocaleString() : article.likes}
    </button>
  );
}

function Meta({ article }: { article: Article }) {
  return (
    <div className="flex items-center gap-4 text-xs text-foreground-500">
      <span className="inline-flex items-center gap-1">
        <i className="ri-calendar-line" />
        {article.date}
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="ri-eye-line" />
        {article.views} 围观
      </span>
    </div>
  );
}

interface Props {
  category: Category;
  articles: Article[];
  index: number;
}

export default function CategorySection({ category, articles, index }: Props) {
  const variantEven = index % 2 === 0;
  const bg = variantEven ? "bg-background-50" : "bg-background-100/70";

  return (
    <section id={category.key} className={`${bg} scroll-mt-20 py-14 md:py-20`}>
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className={`flex flex-col gap-3 md:flex-row md:items-end md:justify-between`}>
          <div>
            <span className="flex items-center gap-2 text-sm font-bold tracking-wide text-primary-600">
              <i className="ri-hashtag" />
              栏目 {String(index + 1).padStart(2, "0")}
            </span>
            <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
              {category.name}
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {category.desc}
            </p>
          </div>
          <a
            href="#subscribe"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-primary-700 transition hover:bg-primary-100"
          >
            更多该栏目赛报
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        {variantEven ? (
          <div className="mt-8 grid gap-6">
            {articles.map((article, i) =>
              i === 0 ? (
                <article
                  key={article.id}
                  className="grid overflow-hidden rounded-3xl border border-background-200 bg-background-50 lg:grid-cols-2"
                >
                  <div className="relative min-h-60 lg:min-h-full">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="absolute inset-0 h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="flex flex-col justify-center p-6 md:p-9">
                    <span className="inline-flex w-fit items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                      <i className="ri-fire-fill" />
                      {article.tag}
                    </span>
                    <h3 className="mt-4 text-xl font-bold leading-snug text-foreground-900 md:text-2xl">
                      {article.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
                    <div className="mt-6 flex items-center justify-between border-t border-background-200 pt-4">
                      <Meta article={article} />
                      <LikeButton article={article} />
                    </div>
                  </div>
                </article>
              ) : (
                <article
                  key={article.id}
                  className="grid gap-5 rounded-3xl border border-background-200 bg-background-50 p-4 md:grid-cols-[220px_1fr] md:items-center md:p-5"
                >
                  <img
                    src={article.image}
                    alt={article.title}
                    className="h-40 w-full rounded-2xl object-cover object-top md:h-32 md:w-52"
                  />
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
                        {article.tag}
                      </span>
                    </div>
                    <h3 className="mt-2 text-base font-bold leading-snug text-foreground-900 md:text-lg">
                      {article.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{article.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <Meta article={article} />
                      <LikeButton article={article} />
                    </div>
                  </div>
                </article>
              )
            )}
          </div>
        ) : (
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {articles.map((article) => (
              <article
                key={article.id}
                className="group overflow-hidden rounded-3xl border border-background-200 bg-background-50"
              >
                <div className="relative h-52 overflow-hidden md:h-48">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-3 py-1 text-xs font-semibold text-primary-700 backdrop-blur">
                    {article.tag}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="text-base font-bold leading-snug text-foreground-900 transition group-hover:text-primary-700 md:text-lg">
                    {article.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{article.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between border-t border-background-200 pt-4">
                    <Meta article={article} />
                    <LikeButton article={article} />
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}